import { useAtom, atom } from "jotai";
import { atomFamily, atomWithStorage, useAtomValue } from "jotai/utils";

import * as flowConfig from "flows/eta-estimator/config";

const PREFIX = `${flowConfig.FLOW_NAME}/create`;
const overlayToBeShown = atomWithStorage(`${PREFIX}/show-create-overlay`, true);

export const useOverlayStatus = () => useAtom(overlayToBeShown);

export const useGetOverlayDisplay = () => useAtomValue(overlayToBeShown);

const inputFile = atomWithStorage<string>(`${PREFIX}/input-file`, null);

export const useInputFile = () => useAtom(inputFile);

export const useGetInputFile = () => useAtomValue(inputFile);

const originLatColumn = atomFamily((fileId: string) =>
  atomWithStorage<string>(`${PREFIX}/${fileId}/origin-lat-column`, null)
);

export const useOriginLatColumn = () => {
  const fileId = useAtomValue(inputFile);
  return useAtom(originLatColumn(fileId));
};

export const useGetOriginLatColumn = () => {
  const fileId = useAtomValue(inputFile);
  return useAtomValue(originLatColumn(fileId));
};

const originLongColumn = atomFamily((fileId: string) =>
  atomWithStorage<string>(`${PREFIX}/${fileId}/origin-long-column`, null)
);

export const useOriginLongColumn = () => {
  const fileId = useAtomValue(inputFile);
  return useAtom(originLongColumn(fileId));
};

export const useGetOriginLongColumn = () => {
  const fileId = useAtomValue(inputFile);
  return useAtomValue(originLongColumn(fileId));
};

const destinationLatColumn = atomFamily((fileId: string) =>
  atomWithStorage<string>(`${PREFIX}/${fileId}/dest-lat-column`, null)
);

export const useDestinationLatColumn = () => {
  const fileId = useAtomValue(inputFile);
  return useAtom(destinationLatColumn(fileId));
};

export const useGetDestinationLatColumn = () => {
  const fileId = useAtomValue(inputFile);
  return useAtomValue(destinationLatColumn(fileId));
};

const destinationLongColumn = atomFamily((fileId: string) =>
  atomWithStorage<string>(`${PREFIX}/${fileId}/dest-long-column`, null)
);

export const useDestinationLongColumn = () => {
  const fileId = useAtomValue(inputFile);
  return useAtom(destinationLongColumn(fileId));
};

export const useGetDestinationLongColumn = () => {
  const fileId = useAtomValue(inputFile);
  return useAtomValue(destinationLongColumn(fileId));
};

const startTimeColumn = atomFamily((fileId: string) =>
  atomWithStorage<string>(`${PREFIX}/${fileId}/start-time-column`, null)
);

export const useStartTimeColumn = () => {
  const fileId = useAtomValue(inputFile);
  return useAtom(startTimeColumn(fileId));
};

export const useGetStartTimeColumn = () => {
  const fileId = useAtomValue(inputFile);
  return useAtomValue(startTimeColumn(fileId));
};

const endTimeColumn = atomFamily((fileId: string) =>
  atomWithStorage<string>(`${PREFIX}/${fileId}/end-time-column`, null)
);

export const useEndTimeColumn = () => {
  const fileId = useAtomValue(inputFile);
  return useAtom(endTimeColumn(fileId));
};

export const useGetEndTimeColumn = () => {
  const fileId = useAtomValue(inputFile);
  return useAtomValue(endTimeColumn(fileId));
};

const groupingColumns = atomFamily((fileId: string) =>
  atomWithStorage<string[]>(`${PREFIX}/${fileId}/grouping-columns`, [])
);

export const useGroupingColumns = () => {
  const fileId = useAtomValue(inputFile);
  return useAtom(groupingColumns(fileId));
};

export const useGetGroupingColumns = () => {
  const fileId = useAtomValue(inputFile);
  return useAtomValue(groupingColumns(fileId));
};

const createSpec = atom((get) => {
  const currentFile = get(inputFile);
  const originLat = get(originLatColumn(currentFile));
  const originLong = get(originLongColumn(currentFile));
  const destLat = get(destinationLatColumn(currentFile));
  const destLong = get(destinationLongColumn(currentFile));
  const startTime = get(startTimeColumn(currentFile));
  const endTime = get(endTimeColumn(currentFile));
  if (
    !currentFile ||
    !originLat ||
    !originLong ||
    !destLat ||
    !destLong ||
    !startTime ||
    !endTime
  ) {
    return null;
  }

  return {
    flow: flowConfig.FLOW_NAME,
    data_files: { [flowConfig.INPUT_DATA]: currentFile },
    meta_data: {
      [flowConfig.ORIGIN_LAT_KEY]: originLat,
      [flowConfig.ORIGIN_LONG_KEY]: originLong,
      [flowConfig.DEST_LAT_KEY]: destLat,
      [flowConfig.DEST_LONG_KEY]: destLong,
      [flowConfig.START_TIME_KEY]: startTime,
      [flowConfig.END_TIME_KEY]: endTime,
    },
  };
});

export const useGetCreateSpec = () => useAtomValue(createSpec);

const augmentSpec = atom((get) => {
  if (!get(createSpec)) {
    return null;
  }
  const currentFile = get(inputFile);
  const originLat = get(originLatColumn(currentFile));
  const originLong = get(originLongColumn(currentFile));
  const destLat = get(destinationLatColumn(currentFile));
  const destLong = get(destinationLongColumn(currentFile));
  const startTime = get(startTimeColumn(currentFile));
  const endTime = get(endTimeColumn(currentFile));

  const newColumns = [
    {
      name: flowConfig.Y_NAME,
      type: "arithmetic",
      details: { col1_name: endTime, col2_name: startTime, operation: "-" },
    },
  ];
  const columns_to_drop = [endTime];

  let spec = { new_columns: newColumns, columns_to_drop };

  spec["missing_treatment"] = [
    originLat,
    originLong,
    destLat,
    destLong,
    startTime,
    endTime,
  ].map((column) => ({
    column,
    treatment: "drop-rows",
  }));

  return {
    key: flowConfig.AUGMENTED_FILE,
    output_file_name: `${currentFile}-with-duration`,
    source_data: [currentFile],
    create_type: "AUGMENT",
    create_spec: spec,
  };
});

export const useGetAugmentSpec = () => useAtomValue(augmentSpec);
